import { createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { MentorshipProgramCustomCommunication } from 'redux/schemas/models/mentorship-program-custom-communications';

type MentorshipProgramParams = {
  programId: number;
};

type RequiredCustomCommunicationParams = Pick<
  MentorshipProgramCustomCommunication,
  'type' | 'target' | 'subject' | 'body'
>;

type CreateCustomCommunicationParams = MentorshipProgramParams &
  RequiredCustomCommunicationParams &
  Partial<Pick<MentorshipProgramCustomCommunication, 'enabled'>>;

export const createCustomCommunication = createAsyncThunk<
  MentorshipProgramCustomCommunication,
  CreateCustomCommunicationParams
>(
  'CREATE_MENTORING_PROGRAM_CUSTOM_COMMUNICATION',
  async ({ programId, type, target, subject, body, enabled }) => {
    const response = await axios.post(
      `/mentorship_programs/${programId}/custom_communications`,
      {
        type,
        target,
        subject,
        body,
        enabled,
      }
    );
    return response.data.result as MentorshipProgramCustomCommunication;
  }
);

export const getCustomCommunications = createAsyncThunk<
  MentorshipProgramCustomCommunication[],
  MentorshipProgramParams
>('GET_MENTORING_PROGRAM_CUSTOM_COMMUNICATIONS', async ({ programId }) => {
  const response = await axios.get(
    `/mentorship_programs/${programId}/custom_communications`
  );
  return response.data.result as MentorshipProgramCustomCommunication[];
});

type OptionalCustomCommunicationParams = Partial<
  Pick<
    MentorshipProgramCustomCommunication,
    'type' | 'target' | 'subject' | 'body' | 'enabled'
  >
>;

type UpdateCustomCommunicationParams = MentorshipProgramParams &
  OptionalCustomCommunicationParams & {
    communicationId: number;
  };

export const updateCustomCommunication = createAsyncThunk<
  MentorshipProgramCustomCommunication,
  UpdateCustomCommunicationParams
>(
  'UPDATE_MENTORING_PROGRAM_CUSTOM_COMMUNICATION',
  async ({
    programId,
    communicationId,
    type,
    target,
    subject,
    body,
    enabled,
  }) => {
    const response = await axios.put(
      `/mentorship_programs/${programId}/custom_communications/${communicationId}`,
      {
        type,
        target,
        subject,
        body,
        enabled,
      }
    );
    return response.data.result as MentorshipProgramCustomCommunication;
  }
);

type DeleteCustomCommunicationParams = MentorshipProgramParams & {
  communicationId: number;
};

export const deleteCustomCommunication = createAsyncThunk<
  boolean,
  DeleteCustomCommunicationParams
>(
  'DELETE_MENTORING_PROGRAM_CUSTOM_COMMUNICATION',
  async ({ programId, communicationId }) => {
    const response = await axios.delete(
      `/mentorship_programs/${programId}/custom_communications/${communicationId}`
    );
    return response.data.result;
  }
);

type GetCustomCommunicationPreviewParams = MentorshipProgramParams & {
  communicationId: number;
}

export const getCustomCommunicationPreview = createAsyncThunk<
  Pick<MentorshipProgramCustomCommunication, 'subject' | 'body'> & { from: string },
  GetCustomCommunicationPreviewParams
>(
  'GET_MENTORING_PROGRAM_CUSTOM_COMMUNICATION_PREVIEW',
  async ({ programId, communicationId }) => {
    const response = await axios.get(
      `/mentorship_programs/${programId}/custom_communications/${communicationId}/preview.json`
    );
    return response.data.result;
  }
);
